/*
 * useAuditPanel.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-05 in huWaWi3 33.4.0
 * Copyright (c) 2023, 2024 HUDORA GmbH
 */

import { LadeChecker } from '@hudora/hd-react-components'
import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import reverse from 'lodash.reverse'
import sortBy from 'lodash.sortby'
import React, { ReactElement } from 'react'
import { useQuery } from 'urql'

import { EntityTable3 } from '../../lib/components/ke/EntityTable3'
import { MuiPanel, usePanelMui } from '../../lib/hooks/usePanel'
import { ICommandBarItemProps } from '../../lib/muipanels'
import { AUFTRAGAUDIT_QUERY } from './H2AuditQueries'

export function DisplayNiceH2Audit(props: { nr: string }) {
  const [{ data, fetching, error }] = useQuery({
    query: AUFTRAGAUDIT_QUERY,
    variables: { nr: props.nr },
    pause: !props.nr,
  })
  const auditLog = reverse(sortBy(data?.entity?.auditLog || [], ['created_at', 'designator']))

  return (
    <LadeChecker loading={fetching} error={error} label="Lade Protokoll">
      <Box mb={2}>
        <Typography variant="h4" component="h2">
          Protokoll
        </Typography>
      </Box>
      <Box mb={2}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Zeit</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Nachricht</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLog
                .filter((x) => x.level > '0DBG')
                .map((row) => (
                  <TableRow key={row.designator} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {row?.created_at}
                    </TableCell>
                    <TableCell>{row?.level}</TableCell>
                    <TableCell>{row?.message}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </LadeChecker>
  )
}

export function useH2AuditPanel(nr: string): [ICommandBarItemProps, ReactElement, Function] {
  const [openPanel, panelProps, isPanelOpen, dismissPanel] = usePanelMui('Audit Log')
  const [{ data, fetching, error }] = useQuery({
    query: AUFTRAGAUDIT_QUERY,
    variables: { nr },
    pause: !isPanelOpen,
  })

  const commandBarItem: ICommandBarItemProps = {
    key: 'audit',
    text: 'Audit Log',
    ariaLabel: 'Änderungsverfolgung',
    iconOnly: true,
    iconProps: { iconName: 'Memo', style: { color: 'black' } },
    disabled: false,
    onClick: () => openPanel(),
  }

  const auditLog = data?.entity?.auditLog || []
  const thePanel = isPanelOpen ? (
    <MuiPanel {...panelProps} onDismiss={dismissPanel}>
      <LadeChecker loading={fetching} error={error} label="Lade Audit Log">
        <DisplayH2Audit log={auditLog} />
      </LadeChecker>
    </MuiPanel>
  ) : null

  return [commandBarItem, thePanel, openPanel]
}

export function DisplayH2Audit(props) {
  // Das Stürzt im Chrome (nicht im Safari) ab.
  return (
    <EntityTable3
      title="AuditLogs"
      schemaName="HwAuditLog"
      rowData={props.log}
      hideExcept={['level', 'message', 'created_at', 'created_by2']}
    />
  )
}
